import { notEmpty } from "@orb-labs/types";
function toBigIntValue(value) {
    if (!notEmpty(value))
        return undefined;
    try {
        return typeof value === "bigint" ? value : BigInt(String(value));
    }
    catch {
        return undefined;
    }
}
function timestampsEqual(a, b) {
    if (a instanceof Date && b instanceof Date)
        return a.getTime() === b.getTime();
    return String(a ?? "") === String(b ?? "");
}
function pctChangeFromSeries(startVal, endVal) {
    if (startVal <= BigInt(0) || startVal === endVal)
        return undefined;
    return Number(((endVal - startVal) * BigInt(10000)) / startVal) / 100;
}
function normalizePct(value) {
    if (!notEmpty(value))
        return undefined;
    const n = typeof value === "string" ? Number(value) : value;
    return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}
/**
 * Merge a live price/volume tick into an existing TimeSeriesChange without
 * clobbering the 24h window when the publisher only sends a mid-price update
 * (changeInPercentage: 0, start === end).
 */
// eslint-disable-next-line complexity -- branchy merge rules for mid-tick vs full-window updates
export function mergeLiveTimeSeriesChange(existing, incoming) {
    if (!incoming)
        return existing ?? undefined;
    if (!existing)
        return incoming;
    const incomingStartVal = toBigIntValue(incoming.start?.value);
    const incomingEndVal = toBigIntValue(incoming.end?.value);
    let { changeInPercentage } = incoming;
    const incomingPct = normalizePct(changeInPercentage);
    const existingPct = normalizePct(existing.changeInPercentage);
    const incomingLooksLikeMidTick = incomingPct === 0 &&
        notEmpty(incomingStartVal) &&
        notEmpty(incomingEndVal) &&
        incomingStartVal === incomingEndVal &&
        timestampsEqual(incoming.start?.timestamp, incoming.end?.timestamp);
    const mergedStart = incomingLooksLikeMidTick ? existing.start ?? incoming.start : incoming.start ?? existing.start;
    const mergedEnd = incoming.end ?? existing.end;
    const startVal = toBigIntValue(mergedStart?.value);
    const endVal = toBigIntValue(mergedEnd?.value);
    if (incomingLooksLikeMidTick && notEmpty(existingPct) && existingPct !== 0) {
        changeInPercentage = existingPct;
    }
    else if ((incomingPct === 0 || !Number.isFinite(incomingPct ?? Number.NaN)) &&
        notEmpty(startVal) &&
        notEmpty(endVal) &&
        startVal !== endVal) {
        const recalc = pctChangeFromSeries(startVal, endVal);
        if (notEmpty(recalc))
            changeInPercentage = recalc;
    }
    else if (!Number.isFinite(incomingPct ?? Number.NaN) && notEmpty(existingPct)) {
        changeInPercentage = existingPct;
    }
    let resolvedPct = normalizePct(changeInPercentage);
    if (!notEmpty(resolvedPct) && notEmpty(startVal) && notEmpty(endVal) && startVal !== endVal) {
        resolvedPct = pctChangeFromSeries(startVal, endVal);
    }
    if (!notEmpty(resolvedPct)) {
        resolvedPct = existingPct;
    }
    const finalPct = resolvedPct;
    return {
        start: mergedStart,
        end: mergedEnd,
        ...(notEmpty(finalPct) ? { changeInPercentage: finalPct } : {}),
    };
}
